<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<template>
    <Head title="Sign In" />
    <div class="h-dvh flex flex-col overflow-auto no-scrollbar">
        <main class="flex-1 flex flex-col lg:flex-row">
            <!-- Left: Marketing panel (large screens only in side-by-side mode) -->
            <div class="hidden lg:flex lg:w-[60%] flex-col bg-white px-16 pt-14 pb-6">
                <div class="mb-[clamp(2rem,6vh,5rem)] animate-stagger-in" style="--stagger-delay: 0.15s">
                    <div class="flex items-baseline gap-1.5">
                        <span class="display-serif text-valence-primary-500">{{ wordmarkPrimary }}</span>
                        <span class="body text-valence-grey-500">{{ wordmarkSecondary }}</span>
                    </div>
                </div>

                <h1
                    class="font-serif font-bold text-[clamp(48px,6vw,76px)] leading-[1.05] text-gray-900 mb-[clamp(1rem,2vh,2rem)] animate-stagger-in"
                    style="--stagger-delay: 0.3s">
                    <template v-if="organization_custom_header">{{ organization_custom_header }}</template>
                    <template v-else>Work is a lot.<br />Nadia makes it easier.</template>
                </h1>

                <p class="title-body text-valence-grey-750 mb-[clamp(1rem,3vh,2.5rem)] max-w-2xl animate-stagger-in" style="--stagger-delay: 0.5s">
                    <template v-if="organization_custom_subheader">{{ organization_custom_subheader }}</template>
                    <template v-else>
                        Your AI coach for the moments that matter — the growth plan you've been meaning to start, the review you need to write, the conversation you're not sure how
                        to have.
                    </template>
                </p>

                <div v-if="maintenance_message" class="rounded-lg bg-amber-50 ring-2 ring-inset ring-amber-300 px-4 py-3 body-small text-amber-800 mb-8">
                    {{ maintenance_message }}
                </div>

                <!-- Feature cards -->
                <div class="grid grid-cols-2 gap-5 max-w-[85%]">
                    <div v-for="(card, i) in featureCards" :key="card.question" :style="{ '--stagger-delay': `${0.65 + i * 0.1}s` }" class="feature-card animate-stagger-in">
                        <i class="bi text-valence-primary-500 text-[28px]" :class="card.icon" aria-hidden="true"></i>
                        <p class="body-small text-valence-grey-500 mt-3 mb-1">{{ card.question }}</p>
                        <p class="body-bold text-valence-primary-500">{{ card.answer }}</p>
                    </div>
                </div>

                <!-- Social proof -->
                <div
                    v-if="showSocialProof"
                    class="flex flex-col items-center lg:flex-row gap-2 lg:gap-3 pt-[clamp(1.5rem,4vh,3rem)] animate-stagger-in"
                    style="--stagger-delay: 1.05s">
                    <div class="flex -space-x-2">
                        <img v-for="(src, i) in avatarImages" :key="i" :src="src" alt="" class="w-8 h-8 rounded-full border-2 border-white object-cover" />
                    </div>
                    <p class="body-small text-valence-grey-750">{{ socialProofText }}</p>
                </div>

                <a
                    href="https://www.valence.co/other/privacy"
                    target="_blank"
                    class="details text-valence-grey-600 hover:text-gray-800 transition-colors mt-auto pt-6 self-center animate-stagger-in"
                    style="--stagger-delay: 1.15s">
                    Privacy Policy
                </a>
            </div>

            <!-- Right: Login panel (full-width on mobile, sticky on desktop) -->
            <div
                class="flex-1 lg:w-[40%] lg:sticky lg:top-0 lg:h-dvh bg-valence-primary-100 relative flex flex-col items-center justify-start lg:justify-center px-6 pt-20 pb-10 lg:py-10 lg:px-12">
                <GradientRibbon />
                <div class="w-full max-w-lg flex flex-col items-center relative z-10">
                    <h1 class="lg:hidden display-medium text-gray-900 text-center text-balance mb-8">
                        <template v-if="organization_custom_header">{{ organization_custom_header }}</template>
                        <template v-else>Work is a lot.<br />Nadia makes it easier.</template>
                    </h1>
                    <div class="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-6 lg:p-10 w-full animate-stagger-in" style="--stagger-delay: 0s">
                        <img v-if="organization_logo_url" :src="organization_logo_url" :alt="`${organization_name} Logo`" class="h-10 w-auto mb-6" />
                        <!-- OTP verification (mobile app) -->
                        <template v-if="otp_sent">
                            <h2 class="display-medium text-gray-900 mb-3 text-center lg:text-left">Check your email</h2>
                            <p class="body text-valence-grey-750 mb-2 text-center lg:text-left">Enter the code sent to</p>
                            <p class="body-bold text-gray-900 mb-6 text-center lg:text-left break-all">{{ email }}</p>

                            <form @submit.prevent="onVerifyOtp">
                                <div class="mb-6">
                                    <label for="otp-control">
                                        <span class="sr-only">6-digit code</span>
                                        <input
                                            id="otp-control"
                                            v-model="otp"
                                            type="text"
                                            inputmode="numeric"
                                            maxlength="6"
                                            autocomplete="one-time-code"
                                            placeholder="000000"
                                            :aria-invalid="errors?.otp ? true : undefined"
                                            :aria-describedby="errors?.otp ? 'otp-error' : undefined"
                                            class="login-input text-center text-2xl tracking-[0.3em] font-mono" />
                                    </label>
                                </div>

                                <BaseButton theme="primary" data-testid="verify" class="w-full py-4 rounded-xl font-semibold mb-4" :disabled="otp.length < 6" @click="onVerifyOtp">
                                    Verify
                                </BaseButton>

                                <Transition name="fade" mode="out-in">
                                    <p v-if="errors?.otp" id="otp-error" role="alert" class="details text-red-700 text-center mb-3">
                                        {{ errors.otp }}
                                    </p>
                                </Transition>
                            </form>

                            <div class="flex flex-col items-center gap-2 mt-2">
                                <button v-if="resendTimer > 0" type="button" disabled class="body-small text-valence-grey-500">Resend code in {{ resendTimer }}s</button>
                                <button v-else type="button" class="body-small text-valence-primary-500 hover:underline" @click="onResendOtp">Resend code</button>

                                <button type="button" class="body-small text-valence-grey-500 hover:underline" @click="onChangeEmail">Use a different email</button>
                            </div>
                        </template>

                        <!-- Standard login form -->
                        <template v-else>
                            <h2 class="display-medium text-gray-900 mb-3 text-center lg:text-left">Welcome</h2>
                            <p class="body text-valence-grey-750 mb-8 text-center lg:text-left text-balance">{{ loginWelcomeSubtitle }}</p>

                            <div
                                v-if="notice"
                                role="status"
                                class="body-small text-valence-grey-750 rounded-lg px-4 py-3 bg-gray-50 ring-1 ring-gray-200 mb-6 text-center lg:text-left">
                                {{ notice }}
                            </div>

                            <form v-if="!sso_only" ref="formRef" @submit.prevent="onSubmit">
                                <div class="mb-6">
                                    <label for="email-control">
                                        <span class="sr-only">Work email</span>
                                        <input
                                            id="email-control"
                                            v-model="email"
                                            type="email"
                                            autocomplete="username"
                                            placeholder="name@company.com"
                                            :aria-invalid="formFieldHasError('email') ? true : undefined"
                                            :aria-describedby="formFieldHasError('email') || hasNonFormErrors ? 'email-errors' : undefined"
                                            class="login-input fs-mask" />
                                    </label>
                                </div>

                                <!-- sr-only keeps this persistent live region out of layout. It must stay mounted
                                     and outside the v-if below: a live region that appears together with its own
                                     text is not reliably announced, and the terms step arrives asynchronously
                                     (the partial response to this form) without a focus change. -->
                                <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ termsAnnouncement }}</span>

                                <Transition name="fade" mode="out-in">
                                    <div v-if="showTerms" class="mb-3">
                                        <TermsConsent
                                            v-model:terms="terms"
                                            :terms-content="organization_terms_content"
                                            :modal="modal_terms"
                                            :reconsent-reason="terms_reconsent_reason"
                                            @accept="onSubmit" />
                                        <Transition name="fade" mode="out-in">
                                            <ul v-if="formFieldHasError('terms')" class="error-list mt-1">
                                                <template v-for="(err, i) in errors?.form?.terms" :key="i">
                                                    <li v-text="err"></li>
                                                </template>
                                            </ul>
                                        </Transition>
                                    </div>
                                </Transition>

                                <div v-if="showRememberMe" class="mb-6">
                                    <BaseCheckbox v-model="rememberMe" shape="square" data-testid="remember-me" class="body-small text-valence-grey-750">
                                        Remember my login on this device
                                    </BaseCheckbox>
                                </div>

                                <BaseButton
                                    v-if="!should_show_sso_option && !(modal_terms && showTerms)"
                                    theme="primary"
                                    data-testid="submit"
                                    class="w-full py-4 rounded-xl font-semibold mb-4"
                                    :disabled="showTerms && !terms"
                                    @click="onSubmit"
                                    >Continue</BaseButton
                                >

                                <Transition name="fade" mode="out-in">
                                    <ul v-if="formFieldHasError('email') || hasNonFormErrors" id="email-errors" role="alert" class="error-list mb-3">
                                        <template v-if="formFieldHasError('email')">
                                            <li v-for="(err, i) in errors?.form?.email" :key="i" v-text="err"></li>
                                        </template>
                                        <li v-if="errors?.server_error" v-text="errors?.server_error"></li>
                                        <li v-else-if="errors?.unrecognized_email" v-text="errors?.unrecognized_email"></li>
                                        <li v-else-if="errors?.query" v-text="errors?.query"></li>
                                        <li v-if="errors?.inactive_organization" v-text="errors?.inactive_organization"></li>
                                        <li v-if="errors?.inactive_account" v-text="errors?.inactive_account"></li>
                                        <li v-if="errors?.not_on_allowlist" v-text="errors?.not_on_allowlist"></li>
                                        <li v-if="errors?.mobile_blocked" v-text="errors?.mobile_blocked"></li>
                                    </ul>
                                </Transition>

                                <Transition name="fade" mode="out-in">
                                    <div v-if="email_sent" class="text-center body-small text-valence-grey-750 rounded-lg px-4 py-3 bg-gray-50 ring-1 ring-gray-200 mb-3">
                                        Check your email for a magic sign-in link. Don&apos;t see it?
                                        <button type="button" class="text-valence-primary-500 hover:underline" @click="onResend">Resend it.</button>
                                    </div>
                                </Transition>

                                <Transition name="fade" mode="out-in">
                                    <div v-if="should_show_sso_option" class="flex flex-col gap-2 mb-3">
                                        <BaseButton theme="primary" class="w-full py-4 rounded-xl font-semibold" @click="useSSO = true">Sign in with SSO</BaseButton>
                                        <BaseButton
                                            theme="neutral"
                                            class="w-full py-3 rounded-xl bg-white text-valence-primary-500 font-semibold border border-valence-primary-500 hover:bg-valence-primary-500/5"
                                            @click="useSSO = false"
                                            >Email link</BaseButton
                                        >
                                    </div>
                                </Transition>
                            </form>

                            <template v-if="sso_redirect_url">
                                <div v-if="!sso_only" class="flex items-center gap-3 mt-2 mb-4">
                                    <div class="flex-1 h-px bg-gray-200"></div>
                                    <span class="body-small text-valence-grey-500">or</span>
                                    <div class="flex-1 h-px bg-gray-200"></div>
                                </div>
                                <BaseButton
                                    :theme="sso_only ? 'primary' : 'secondary'"
                                    data-testid="sso-continue"
                                    class="w-full py-4 rounded-xl font-semibold"
                                    @click="onSsoRedirect">
                                    {{ sso_button_label || "Continue with SSO" }}
                                </BaseButton>
                            </template>
                        </template>

                        <div v-if="googleLoaded" class="flex items-center gap-3 my-8">
                            <div class="flex-1 h-px bg-gray-200"></div>
                            <span class="body-small text-valence-grey-500">or</span>
                            <div class="flex-1 h-px bg-gray-200"></div>
                        </div>

                        <div v-show="googleLoaded" class="mb-4">
                            <div id="signinWithGoogle"></div>
                            <div id="g_id_onload" :data-state="googleState" :data-client_id="google_client_id" :data-login_uri="google_login_uri" data-auto_prompt="false"></div>
                            <div
                                class="g_id_signin"
                                data-type="standard"
                                data-size="large"
                                data-theme="outline"
                                data-text="sign_in_with"
                                data-shape="rectangular"
                                data-logo_alignment="center"></div>
                        </div>
                    </div>

                    <p v-if="!sso_only" class="details text-valence-grey-500 text-center mt-6 px-4 animate-stagger-in" style="--stagger-delay: 0.4s">
                        {{ newUserHint }}
                    </p>
                </div>

                <!-- Marketing content below login on mobile/tablet (scroll to see) -->
                <div class="lg:hidden w-full max-w-lg mt-4 px-4 relative z-10">
                    <p class="hidden md:block body text-gray-700 mb-10 text-center text-pretty">
                        <template v-if="organization_custom_subheader">{{ organization_custom_subheader }}</template>
                        <template v-else>
                            Your AI coach for the moments that matter — the growth plan you've been meaning to start, the review you need to write, the conversation you're not sure
                            how to have.
                        </template>
                    </p>

                    <div class="grid grid-cols-2 gap-4">
                        <div v-for="card in featureCards" :key="card.question" class="rounded-xl px-5 py-6 bg-white/30 backdrop-blur-sm">
                            <i class="bi text-valence-primary-500 text-[28px]" :class="card.icon" aria-hidden="true"></i>
                            <p class="body-small text-gray-600 mt-3 mb-1">{{ card.question }}</p>
                            <p class="body-bold text-valence-primary-500">{{ card.answer }}</p>
                        </div>
                    </div>

                    <div v-if="showSocialProof" class="flex flex-col items-center gap-2 pt-10">
                        <div class="flex -space-x-2">
                            <img v-for="(src, i) in avatarImages" :key="i" :src="src" alt="" class="w-8 h-8 rounded-full border-2 border-white object-cover" />
                        </div>
                        <p class="body-small text-gray-600">{{ socialProofText }}</p>
                    </div>

                    <a
                        href="https://www.valence.co/other/privacy"
                        target="_blank"
                        class="block details text-valence-grey-600 hover:text-gray-800 transition-colors text-center pt-8 pb-6">
                        Privacy Policy
                    </a>
                </div>
            </div>
            <!-- Mobile SSO loading overlay -->
            <Transition name="fade">
                <div v-if="ssoLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm">
                    <div class="text-center">
                        <div class="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-valence-primary-200 border-t-valence-primary-500"></div>
                        <p class="body text-valence-grey-750">Signing in via SSO…</p>
                    </div>
                </div>
            </Transition>
            <!-- LAB-1004: old-binary bridge failure. Prompt to update rather than hang. -->
            <Transition name="fade">
                <div v-if="updateRequired" class="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm p-6">
                    <div class="max-w-sm rounded-xl bg-white p-6 text-center shadow-lg">
                        <p class="title-section mb-2 text-valence-grey-950">Update Nadia to continue</p>
                        <p class="body mb-6 text-valence-grey-750">This version of Nadia can't complete SSO sign-in. Update to the latest version from Google Play.</p>
                        <a :href="PLAY_STORE_URL" target="_blank" rel="noopener noreferrer" class="base-btn-primary">Open Google Play</a>
                    </div>
                </div>
            </Transition>
        </main>
    </div>
    <CookieBanner />
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import { Capacitor } from "@capacitor/core";
import { Head, router } from "@inertiajs/vue3";
import { useScriptTag } from "@vueuse/core";
import Profile1Image from "~img/profile-1.png";
import Profile2Image from "~img/profile-2.png";
import Profile3Image from "~img/profile-3.png";
import Profile4Image from "~img/profile-4.png";
import Profile5Image from "~img/profile-5.png";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import CookieBanner from "~vue/components/CookieBanner.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import GradientRibbon from "~vue/components/GradientRibbon.vue";
import TermsConsent from "~vue/components/TermsConsent.vue";
import { stripSkipAutoSsoMarker } from "~vue/skipAutoSso";
import { termsConsentAnnouncement } from "~vue/utils/termsConsentAnnouncement";
import { computed, onUnmounted, ref, useTemplateRef, watch } from "vue";

/* Django form errors are arrays — each field can have multiple validation messages */
interface FormErrors {
    form?: { email?: string[]; terms?: string[] };
    server_error?: string;
    unrecognized_email?: string;
    query?: string;
    inactive_organization?: string;
    inactive_account?: string;
    not_on_allowlist?: string;
    mobile_blocked?: string;
    otp?: string;
}

type FormValue = {
    email: string;
    terms?: boolean;
    terms_document_id?: string;
    chose_sso?: boolean;
    otp?: string;
    remember_me?: boolean;
};

type LoginPayload = {
    intent: "authenticate" | "resend_link" | "verify_otp" | "resend_otp";
    user_timezone?: string;
    form: FormValue;
};

interface FeatureCard {
    question: string;
    answer: string;
    icon: string;
}

interface LandingCustomization {
    wordmark_primary: string | null;
    wordmark_secondary: string | null;
    social_proof_text: string | null;
    hide_social_proof: boolean;
    login_welcome_subtitle: string | null;
    new_user_hint: string | null;
    feature_cards: FeatureCard[] | null;
}

interface Props {
    email_sent: boolean;
    should_show_terms: boolean;
    modal_terms?: boolean;
    should_show_sso_option: boolean;
    email: string;
    errors: FormErrors;
    notice?: string;
    organization_name?: string;
    organization_logo_url?: string;
    organization_custom_header?: string;
    organization_custom_subheader?: string;
    organization_terms_content?: string;
    terms_document_id?: string;
    /** Why the terms prompt is shown (server-computed); selects the re-consent copy variant. */
    terms_reconsent_reason?: string;
    organization_landing_customization?: LandingCustomization | null;
    google_client_id?: string;
    google_login_uri?: string;
    maintenance_message?: string;
    is_mobile_app?: boolean;
    otp_sent?: boolean;
    sso_redirect_url?: string;
    /**
     * BUG-923: set by the server on the post-logout branded page of an auto-SSO org.
     * Hides the email/magic-link form so SSO is the only offered path.
     */
    sso_only?: boolean;
    /** Org-customizable label for the SSO button, sent only alongside `sso_only`. */
    sso_button_label?: string;
    mobile_sso_url?: string;
    /**
     * LAB-617: set by the server when the WebView lands on this region via the
     * cross-cluster handoff (?mobile_handoff=1&email=…). Triggers an automatic
     * `authenticate` POST so the user does not have to re-tap Continue.
     */
    auto_authenticate?: boolean;
    /** False when the org has turned "Remember me" off; hides the box (server clears the cookie). */
    remember_me_enabled?: boolean;
    /** Reflects the existing cookie, so a returning user sees the box already ticked. */
    remember_me_checked?: boolean;
}

const props = defineProps<Props>();

const DEFAULT_FEATURE_CARDS: readonly FeatureCard[] = [
    { question: "Ready to grow?", answer: "Nadia builds your development plan", icon: "bi-rocket-takeoff" },
    { question: "Review season again?", answer: "Nadia helps you write a strong self-eval", icon: "bi-pencil-square" },
    { question: "Tough feedback to give?", answer: "Nadia helps you find the right words", icon: "bi-chat-dots" },
    { question: "Too much on your plate?", answer: "Nadia helps you prioritize", icon: "bi-signpost-split" },
];
const DEFAULT_WORDMARK_PRIMARY = "Nadia";
const DEFAULT_WORDMARK_SECONDARY = "by Valence";
const DEFAULT_SOCIAL_PROOF = "Join 50,000+ professionals who use Nadia every week";
const DEFAULT_LOGIN_WELCOME_SUBTITLE = "Sign in to continue your journey with Nadia";
const DEFAULT_NEW_USER_HINT = "New here? Your company gave you access to Nadia — enter your work email to get started.";

const wordmarkPrimary = computed(() => props.organization_landing_customization?.wordmark_primary || DEFAULT_WORDMARK_PRIMARY);
const wordmarkSecondary = computed(() => props.organization_landing_customization?.wordmark_secondary || DEFAULT_WORDMARK_SECONDARY);
const socialProofText = computed(() => props.organization_landing_customization?.social_proof_text || DEFAULT_SOCIAL_PROOF);
const showSocialProof = computed(() => !props.organization_landing_customization?.hide_social_proof);
const loginWelcomeSubtitle = computed(() => props.organization_landing_customization?.login_welcome_subtitle || DEFAULT_LOGIN_WELCOME_SUBTITLE);
const newUserHint = computed(() => props.organization_landing_customization?.new_user_hint || DEFAULT_NEW_USER_HINT);
const featureCards = computed<readonly FeatureCard[]>(() => props.organization_landing_customization?.feature_cards || DEFAULT_FEATURE_CARDS);

const avatarImages = [Profile1Image, Profile2Image, Profile3Image, Profile4Image, Profile5Image];

// Remember-me: the cookie is httponly, so the server owns both the stored value and the
// prefill (props.email). The box only reports intent back on submit.
const rememberMe = ref(props.remember_me_checked ?? false);

const email = ref(props.email);
const terms = ref(false);
const useSSO = ref<boolean | undefined>(undefined);
const googleLoaded = ref(false);
const formRef = useTemplateRef("formRef");

// OTP state (mobile app only)
const OTP_RESEND_COOLDOWN_SECONDS = 60;
const otp = ref("");
const resendTimer = ref(0);
let resendInterval: ReturnType<typeof setInterval> | null = null;

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const googleState = JSON.stringify({ timezone: userTimezone });

// Mobile SSO state
const ssoLoading = ref(false);

const hasErrors = computed(() => Object.keys(props.errors).length > 0);
const hasNonFormErrors = computed(() => hasErrors.value && !props.errors?.form);

// The terms are rendered by the server for a specific email (props.email). If the user
// edits the email input, the shown terms no longer apply to what they're submitting, so
// hide the consent UI until they re-submit and the server re-evaluates for the new address.
const showTerms = computed(() => props.should_show_terms && email.value === props.email);
const termsAnnouncement = computed(() => (showTerms.value ? termsConsentAnnouncement(props.terms_reconsent_reason) : ""));

// Offered on mobile too: the app's users type their email into this same form before the
// OTP step, so the prefill helps them identically.
const rememberMeAvailable = computed(() => props.remember_me_enabled !== false);

// There is nothing to remember until an email has been typed, so the box only appears once
// the field has content. Kept separate from `rememberMeAvailable` because the submit still
// has to report the unticked state — see onSubmit.
const showRememberMe = computed(() => rememberMeAvailable.value && email.value.trim() !== "");

// Revoke as soon as the box is unticked, rather than waiting for a submit that may never
// come: a user who unticks and walks away (or signs in through a path that doesn't post this
// form) would otherwise keep the cookie and find the box ticked again next visit. Ticking is
// not mirrored here — the cookie needs the email and resolved org, so it is written on submit.
watch(rememberMe, (isRemembering) => {
    if (!isRemembering) {
        forgetRememberedLogin();
    }
});

async function forgetRememberedLogin() {
    // Best-effort: the submit path clears it too, so a failure here is not user-visible and
    // must not block the login the user is in the middle of.
    const { error } = await useFetch("/accounts/forget-remembered-login/", { method: "DELETE" });
    if (error.value) {
        console.error("Failed to clear the remembered login", error.value);
    }
}

// BUG-923: client-side strip via Inertia router.replace (see skipAutoSso.ts).
stripSkipAutoSsoMarker();

if (props.google_client_id) {
    useScriptTag("https://accounts.google.com/gsi/client", () => {
        googleLoaded.value = true;
    });
}

watch(
    () => props.email,
    (val, prev) => {
        if (prev && val !== prev) {
            terms.value = false;
            useSSO.value = undefined;
        }
    },
);

watch(useSSO, () => {
    formRef.value?.dispatchEvent(new Event("submit"));
});

function formFieldHasError(field: string) {
    return hasErrors.value && props.errors?.form && Object.hasOwn(props.errors.form, field);
}

function postResponse(payload: LoginPayload, propsToHydrate: string[] = []) {
    router.post("", payload, {
        only: propsToHydrate,
        headers: { "X-CSRFToken": getCookie("csrftoken") ?? "" },
    });
}

function onResend() {
    postResponse({ intent: "resend_link", form: { email: email.value } }, ["email_sent", "errors", "maintenance_message"]);
}

function onSubmit() {
    const formValue: FormValue = { email: email.value };

    // Gated on `rememberMeAvailable`, not `showRememberMe`: the field always has content by
    // the time a submit lands, and sending the unticked state is what tells the server to
    // clear an existing cookie rather than leave it alone.
    if (rememberMeAvailable.value) {
        formValue.remember_me = rememberMe.value;
    }

    if (props.email === formValue.email) {
        if (props.should_show_terms || props.should_show_sso_option) {
            formValue.terms = terms.value;
            if (terms.value) {
                formValue.terms_document_id = props.terms_document_id;
            }
        }
        if (props.should_show_sso_option) {
            formValue.chose_sso = useSSO.value;
        }
    }

    postResponse({ intent: "authenticate", user_timezone: userTimezone, form: formValue }, [
        "email",
        "email_sent",
        "otp_sent",
        "errors",
        "should_show_terms",
        "should_show_sso_option",
        "organization_terms_content",
        "terms_document_id",
        "terms_reconsent_reason",
        "modal_terms",
        "maintenance_message",
        "mobile_sso_url",
        "remember_me_enabled",
        "remember_me_checked",
    ]);

    if (props.is_mobile_app) {
        startResendTimer();
    }
}

// OTP methods (mobile app only)
function startResendTimer() {
    resendTimer.value = OTP_RESEND_COOLDOWN_SECONDS;
    if (resendInterval) {
        clearInterval(resendInterval);
    }
    resendInterval = setInterval(() => {
        if (resendTimer.value > 0) {
            resendTimer.value--;
        } else if (resendInterval) {
            clearInterval(resendInterval);
            resendInterval = null;
        }
    }, 1000);
}

function onVerifyOtp() {
    postResponse({ intent: "verify_otp", form: { email: email.value, otp: otp.value } }, ["email", "otp_sent", "errors"]);
}

function onResendOtp() {
    postResponse({ intent: "resend_otp", form: { email: email.value } }, ["otp_sent", "errors"]);
    startResendTimer();
}

function onChangeEmail() {
    otp.value = "";
    router.get("");
}

function onSsoRedirect() {
    if (props.sso_redirect_url) {
        globalThis.location.href = props.sso_redirect_url;
    }
}

// Mobile SSO: open the SSO URL in the system browser (SFSafariViewController on iOS,
// Chrome Custom Tab on Android).  After SSO completes the server issues a 302 to our
// co.valence.coach:// deep link, which the appUrlOpen listener in vue.js handles.
let browserListenerHandle: { remove: () => Promise<void> } | null = null;

// LAB-1004: on Android app binaries built before the cross-origin bridge fix,
// navigation to a non-primary origin (e.g. coach-eu.valence.co after LAB-617
// handoff) loses the Capacitor JS bridge and every plugin call throws
// "not implemented on android". Detect that state and prompt the user to
// update rather than silently hanging on Browser.open forever.
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=co.valence.nadia";
const updateRequired = ref(false);

async function openMobileSso(url: string) {
    if (!Capacitor.isNativePlatform()) {
        return;
    }
    if (!Capacitor.isPluginAvailable("Browser")) {
        updateRequired.value = true;
        return;
    }
    ssoLoading.value = true;
    try {
        const { Browser } = await import("@capacitor/browser");
        if (browserListenerHandle) {
            await browserListenerHandle.remove();
        }
        browserListenerHandle = await Browser.addListener("browserFinished", () => {
            ssoLoading.value = false;
        });
        await Browser.open({ url });
    } catch {
        ssoLoading.value = false;
        updateRequired.value = true;
    }
}

// Trigger when mobile_sso_url is set — either on initial render (auto-SSO orgs)
// or after Inertia partial reload (email-submit SSO).
watch(
    () => props.mobile_sso_url,
    (url) => {
        if (url) {
            openMobileSso(url);
        }
    },
    { immediate: true },
);

// LAB-617 cross-cluster mobile handoff: if the WebView landed here from the
// other deployment with the handoff marker, kick off `authenticate` immediately
// so the user sees the OTP screen without having to tap Continue again.
if (props.auto_authenticate && props.is_mobile_app && email.value) {
    onSubmit();
}

onUnmounted(() => {
    if (resendInterval) {
        clearInterval(resendInterval);
    }
    if (browserListenerHandle) {
        browserListenerHandle.remove().catch(() => {});
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

/* Hide scrollbar but allow scrolling */
.no-scrollbar {
    scrollbar-width: none;
}

.login-input {
    @apply w-full px-4 py-4 rounded-xl border border-gray-200 body text-gray-900 placeholder:text-gray-400 outline-none focus:border-valence-primary-500 focus:ring-1 focus:ring-valence-primary-500 transition-colors;
}

.feature-card {
    @apply rounded-xl px-6 bg-valence-primary-100;
    padding-block: clamp(1rem, 2vh, 1.75rem);
}

.error-list li {
    @apply details text-red-700 text-center;
}

.g_id_signin :deep(*[role="button"]) {
    @apply rounded-lg;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
